import { createAuthClient } from "better-auth/svelte";
import {
    organizationClient,
    adminClient,
    multiSessionClient,
    magicLinkClient,
    genericOAuthClient,
    twoFactorClient,
    inferAdditionalFields,
    emailOTPClient,
} from "better-auth/client/plugins";
import { apiKeyClient } from "@better-auth/api-key/client";
import { ssoClient } from "@better-auth/sso/client";
import { scimClient } from "@better-auth/scim/client";
import { i18nClient } from "@better-auth/i18n/client";
import { ac, roles, orgAC, orgRoles } from "@repo/backend/accessControl";
import type { acquireAuth } from "@repo/backend/auth";
import { resolve } from "./route.svelte.js";
import { currentRelativeURL } from "./url.svelte.js";
import { notNullOrDie } from "@repo/utils";

type ServerAuth = Awaited<ReturnType<typeof acquireAuth>>;

export const auth = createAuthClient({
    plugins: [
        inferAdditionalFields<ServerAuth>(),
        organizationClient<{
            ac: typeof orgAC;
            roles: typeof orgRoles;
            dynamicAccessControl: { enabled: true };
            teams: { enabled: true };
        }>({
            ac: orgAC,
            roles: orgRoles,
            dynamicAccessControl: { enabled: true },
            teams: { enabled: true },
        }),
        adminClient({
            ac,
            roles,
        }),
        multiSessionClient(),
        magicLinkClient(),
        ssoClient(),
        scimClient(),
        genericOAuthClient(),
        twoFactorClient(),
        emailOTPClient(),
        i18nClient(),
        apiKeyClient(),
    ],
});

export type Auth = typeof auth;
export type Session = Auth["$Infer"]["Session"];
export type Team = Auth["$Infer"]["Team"];
// better-auth's `inferAdditionalFields` does not propagate into `Member.user`,
// and the server's `getFullOrganization` after-hook enriches user + filters admins out
// (see apps/backend/src/server/auth.ts hook on getFullOrganization).
export type Member = Omit<Auth["$Infer"]["Member"], "user" | "role"> & {
    user: Auth["$Infer"]["Member"]["user"] & { firstName: string; lastName: string };
    role: "manager" | "member";
};
// `domains` is declared as an organization additionalField server-side but
// `inferAdditionalFields` does not pick up organization-level additional fields.
export type ActiveOrganization = Omit<Auth["$Infer"]["ActiveOrganization"], "members"> & {
    domains: string[];
    teams: Team[];
    teamMembers: { userId: string; teamId: string }[];
    members: Member[];
};

export function useSession() {
    const store = auth.useSession();
    const hmr = import.meta.hot?.data;
    let initial = store.get().data;
    if (hmr) {
        if (initial) {
            hmr.session = initial;
        } else {
            initial = hmr.session as NonNullable<typeof initial>;
        }
    }
    const session = $state(notNullOrDie(initial));
    $effect(() => {
        return store.listen(s => {
            if (hmr && s.data) {
                hmr.session = s.data;
            }
            Object.assign(session, s.data);
        });
    });
    return session;
}

export function useOrganization(): ActiveOrganization {
    const store = auth.useActiveOrganization();
    const hmr = import.meta.hot?.data;
    let initial = store.get().data;
    if (hmr) {
        if (initial) {
            hmr.organization = initial;
        } else {
            initial = hmr.organization as NonNullable<typeof initial>;
        }
    }
    const organization = $state(notNullOrDie(initial)) as ActiveOrganization;
    $effect(() => {
        return store.listen(s => {
            if (hmr && s.data) {
                hmr.organization = s.data;
            }
            Object.assign(organization, s.data);
        });
    });
    return organization;
}

export async function refetchOrganization() {
    const store = auth.useActiveOrganization();
    await store.value.refetch();
}

export async function useMemberPermissions(
    permissions: Parameters<typeof auth.organization.hasPermission>[0]["permissions"],
) {
    const x = await auth.organization.hasPermission({ permissions });
    if (x.error || x.data.error) {
        throw new Error(JSON.stringify(x.error ?? x.data.error));
    }
    return x.data.success;
}

export async function signOut() {
    await auth.signOut({
        fetchOptions: {
            onSuccess() {
                window.location.assign(resolve("/signin", {}, { dest: currentRelativeURL() }));
            },
        },
    });
}
